import { useMemo } from 'react';
import { Layer, Source } from 'react-map-gl/maplibre';
import type { CircleLayerSpecification, LineLayerSpecification } from 'maplibre-gl';
import { useGameStore } from '@/state/gameStore';

const HYDRANT_LAYER: CircleLayerSpecification = {
  id: 'hydrants',
  type: 'circle',
  source: 'hydrants',
  minzoom: 13,
  paint: {
    // Phase 11.1.1 (N2) — Maplibre erlaubt nur EINE zoom-based interpolate-
    // Expression auf top level. Alte Variante hatte verschachtelte
    // `case + interpolate(zoom)` → Konsolen-Errors. Jetzt: single
    // `interpolate(zoom)` mit `case`-Werten an jedem Stop.
    'circle-radius': [
      'interpolate',
      ['linear'],
      ['zoom'],
      13,
      ['case', ['get', 'occupied'], 4, 2],
      17,
      ['case', ['get', 'occupied'], 9, 6],
    ],
    'circle-color': ['case', ['get', 'occupied'], '#0ea5e9', '#3b82f6'],
    'circle-opacity': 0.85,
    'circle-stroke-color': ['case', ['get', 'occupied'], '#7dd3fc', '#ffffff'],
    'circle-stroke-width': ['case', ['get', 'occupied'], 2, 0.8],
    'circle-stroke-opacity': 0.85,
  },
};

const CONNECTION_LAYER: LineLayerSpecification = {
  id: 'hydrant-connections',
  type: 'line',
  source: 'hydrant-connections',
  paint: {
    'line-color': '#3b82f6',
    'line-opacity': 0.7,
    'line-width': 2,
    'line-dasharray': [1.5, 1.5],
  },
};

/**
 * Renders fire hydrants on the map and dashed connection lines between
 * water-pumping vehicles in Status 4 and the hydrant they're feeding from.
 */
export function HydrantLayer() {
  const visible = useGameStore((s) => s.uiPrefs.layerToggles.hydrants);
  const hydrants = useGameStore((s) => s.hydrants);
  const vehicles = useGameStore((s) => s.vehicles);

  // Phase 7.2.3 — track which hydrants are occupied (any vehicle in Status 4
  // currently feeding from them) and how many vehicles share each.
  const hydrantOccupancy = useMemo(() => {
    const map = new Map<string, number>();
    for (const v of vehicles) {
      if (v.status !== 4 || !v.connectedHydrantId) continue;
      map.set(v.connectedHydrantId, (map.get(v.connectedHydrantId) ?? 0) + 1);
    }
    return map;
  }, [vehicles]);

  const hydrantGeoJson = useMemo(() => {
    if (!visible) return null;
    return {
      type: 'FeatureCollection' as const,
      features: hydrants.map((h) => ({
        type: 'Feature' as const,
        geometry: { type: 'Point' as const, coordinates: [h.position.lng, h.position.lat] },
        properties: {
          id: h.id,
          source: h.source,
          occupied: hydrantOccupancy.has(h.id),
          users: hydrantOccupancy.get(h.id) ?? 0,
        },
      })),
    };
  }, [hydrants, visible, hydrantOccupancy]);

  const connectionGeoJson = useMemo(() => {
    const features: Array<{
      type: 'Feature';
      geometry: { type: 'LineString'; coordinates: [number, number][] };
      properties: Record<string, unknown>;
    }> = [];
    if (!visible) return { type: 'FeatureCollection' as const, features };
    const hyById = new Map(hydrants.map((h) => [h.id, h]));
    for (const v of vehicles) {
      if (v.status !== 4) continue;
      if (!v.connectedHydrantId) continue;
      const h = hyById.get(v.connectedHydrantId);
      if (!h) continue;
      features.push({
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [
            [v.position.lng, v.position.lat],
            [h.position.lng, h.position.lat],
          ],
        },
        properties: { vehicleId: v.id },
      });
    }
    return { type: 'FeatureCollection' as const, features };
  }, [hydrants, vehicles, visible]);

  if (!visible) return null;

  return (
    <>
      {hydrantGeoJson && hydrantGeoJson.features.length > 0 && (
        <Source id="hydrants" type="geojson" data={hydrantGeoJson}>
          <Layer {...HYDRANT_LAYER} />
        </Source>
      )}
      {connectionGeoJson.features.length > 0 && (
        <Source id="hydrant-connections" type="geojson" data={connectionGeoJson}>
          <Layer {...CONNECTION_LAYER} />
        </Source>
      )}
    </>
  );
}
